import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { AlgoliaHitResult } from "components/_dashboard/listingsMatch/algoliaClient";
import { fetchListingById } from "utils/listings/fetchListings";
import { realEstateListingsCollection } from "utils/listings/collections";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import {
  ListingDetailsDialog,
  ListingDetailsTabType
} from "components/_dashboard/listings/modal/dialog/ListingDetailsDialog";
import { NewListingDialog } from "components/_dashboard/listings/modal/dialog/NewListingDialog";
import type { ReactNode } from "react";
import type { ListingWithAdditionalData } from "./../@types/listings/new";

export type OpenListingDialogParams = {
  listingId?: string;
  algoliaListing?: AlgoliaHitResult;
  source?: "algolia" | "webot";
  initialTab?: ListingDetailsTabType;
  /**
   * OPT-IN. Ask for the listing's REAL `real-estate-listings` document rather
   * than the `yad2-listings-latest` mirror row, so the dialog opens with edit
   * capabilities instead of read-only.
   *
   * ## Why this is a flag and not the default
   *
   * ONE dialog serves the whole app from this provider — the listings board, the
   * matches surfaces, `ListingMatchChip`, the creatives and makeovers entry
   * points, `OpenListingTriggerPage`. Making the webot branch unconditional
   * would change which STORE every one of those reads from, and with it which
   * tabs they get (`MatchedBuyersTab` vs `MatchedBuyersTabForWebot`). Those
   * surfaces are deliberately untouched: they pass nothing and keep the exact
   * behaviour they had.
   *
   * ## Why it is not "just convert the hit"
   *
   * `convertProcessedListingDataToListing(hit)` is typed
   * `Omit<Listing, "id" | "owners" | "ownersData" | …>` and hardcodes fee, files,
   * exclusivity, priceHistory and additionalNotes to null — and the form's save
   * is a whole-document `update()`. Handing that to an EDITABLE dialog would
   * blank `owners` (orphaning the listing), delete its files, wipe exclusivity
   * and destroy priceHistory on the first save. The real document, or read-only.
   * There is no third option.
   *
   * Ignored for any row whose `listingOrigin` is not `webot`.
   */
  editable?: boolean;
};

type ListingDialogContextValue = {
  openListingDialog: (params: OpenListingDialogParams) => Promise<void>;
};

const ListingDialogContext = createContext<ListingDialogContextValue>({
  openListingDialog: async () => undefined
});

type Viewer = {
  userEmail?: string;
  organizationId?: string;
  isAdminOfTheOrganization?: boolean;
};

/**
 * May this viewer edit this listing?
 *
 * Transcribed from `ListingTableRow` (the listings PAGE), deliberately verbatim
 * so the board cannot disagree with the page about who may edit what: an admin
 * of the organization that created it, or one of its owners.
 */
function mayEditListing(listing: ListingWithAdditionalData, viewer: Viewer): boolean {
  const isInTheSameOrganization = listing.createdByOrganization
    ? listing.createdByOrganization === viewer.organizationId
    : false;
  const isAdminInTheOrganization = isInTheSameOrganization
    ? !!viewer.isAdminOfTheOrganization
    : false;
  const isOwner = !!viewer.userEmail && (listing.owners ?? []).includes(viewer.userEmail);

  return isAdminInTheOrganization || isOwner;
}

export function ListingDialogProvider({ children }: { children: ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [algoliaListing, setAlgoliaListing] = useState<AlgoliaHitResult | undefined>(undefined);
  const [webotListing, setWebotListing] = useState<ListingWithAdditionalData | undefined>(undefined);
  const [canEditWebotListing, setCanEditWebotListing] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [initialTab, setInitialTab] = useState<ListingDetailsTabType>("general");
  const [editOpen, setEditOpen] = useState(false);
  const [editDuplicating, setEditDuplicating] = useState(false);

  const {
    user: { email: userEmail }
  } = useAuth();
  const { organizationId, isAdminOfTheOrganization } = useFirestore();

  // Read through a ref so `openListingDialog` keeps an EMPTY dependency list and
  // therefore a permanently stable identity. Every board row holds it inside a
  // `useCallback` dep array and the rows are `React.memo`'d — a new identity
  // here re-renders all ~200 of them, which is the precise cost that
  // memoization was added to avoid (see the notes on `ListingsBoardRow`).
  const viewerRef = useRef<Viewer>({ userEmail, organizationId, isAdminOfTheOrganization });
  viewerRef.current = { userEmail, organizationId, isAdminOfTheOrganization };

  // Bumped on every open and on close, so a slow document read can never land
  // on top of a newer open (or a dialog the user has already dismissed).
  const requestRef = useRef(0);

  const resolveWebotListing = useCallback(
    async (listingId: string): Promise<ListingWithAdditionalData | null> => {
      try {
        const snapshot = await getDoc(doc(realEstateListingsCollection, listingId));
        if (!snapshot.exists()) return null;
        const data = snapshot.data();
        return data ? ({ ...data, id: data.id ?? listingId } as ListingWithAdditionalData) : null;
      } catch (error) {
        console.error("openListingDialog: could not read the listing document", {
          listingId,
          error
        });
        return null;
      }
    },
    []
  );

  const openListingDialog = useCallback(
    async ({
      listingId,
      algoliaListing: providedAlgoliaListing,
      source = "algolia",
      initialTab: tab = "general",
      editable = false
    }: OpenListingDialogParams): Promise<void> => {
      const request = ++requestRef.current;

      // The id of the REAL document, when the caller asked for an editable open
      // AND the row genuinely is a webot one.
      //
      // A webot mirror row carries its `real-estate-listings` id as its OWN
      // document id — the mirror Cloud Function writes
      // `yad2-listings-latest/${listingId}` — so the hit's objectID already IS
      // the id to read. No new endpoint, no lookup table, and it works for a
      // listing created seconds ago whose mirror row does not exist yet.
      const webotListingId = editable
        ? providedAlgoliaListing
          ? providedAlgoliaListing.listingOrigin === "webot"
            ? providedAlgoliaListing.objectID ?? providedAlgoliaListing.id
            : undefined
          : listingId
        : undefined;

      if (webotListingId) {
        // Open first and resolve behind a spinner. `ListingDetailsDialog`
        // already renders one for exactly this case (`isResolvingListing` with
        // neither listing present); showing the mirror row in the meantime would
        // paint the read-only general tab and then swap it for the editable one.
        setAlgoliaListing(undefined);
        setWebotListing(undefined);
        setCanEditWebotListing(false);
        setInitialTab(tab);
        setIsResolving(true);
        setOpen(true);

        const listing = await resolveWebotListing(webotListingId);
        if (requestRef.current !== request) return;

        if (listing) {
          setWebotListing(listing);
          setCanEditWebotListing(mayEditListing(listing, viewerRef.current));
        } else {
          // Deleted, or unreadable. Fall back to the row we were handed so the
          // realtor still sees the listing — read-only — instead of a dialog
          // that resolved to nothing.
          setAlgoliaListing(providedAlgoliaListing);
        }

        setIsResolving(false);
        return;
      }

      // ---- The mirror-row path. ----

      // Nothing to open and nothing to look up: a true no-op, so it must not
      // touch the state of a dialog that is already on screen.
      if (!providedAlgoliaListing && !listingId) {
        console.warn("openListingDialog: listing not found", { listingId, source });
        return;
      }

      // Clearing the webot document here rather than on close is deliberate: a
      // stale one would make `isViewMode` false for the NEXT mirror-row open and
      // render the previous listing's general tab. Close keeps its content so
      // the dialog does not blank out mid fade-out.
      setWebotListing(undefined);
      setCanEditWebotListing(false);
      setInitialTab(tab);

      // ⚠️ OPEN FIRST, RESOLVE BEHIND THE SPINNER — the same shape as the
      // editable branch above, and for a reported reason.
      //
      // This branch used to `await fetchListingById(listingId)` and only then
      // `setOpen(true)`. Every caller that hands us an id without a row — the
      // dashboard notification widgets, `ListingMatchChip`, `OpenListingTriggerPage`
      // — therefore rendered NOTHING AT ALL between the click and the answer:
      // no dialog, no spinner, no disabled state. One point read against
      // `yad2-listings-latest` plus `withRetries`' 100/200/400ms backoff is fast
      // on a warm connection and seconds on a cold or flaky one, and the whole
      // wait was invisible. Reported as "it takes a good few seconds to open the
      // listing modal from the dashboard widgets".
      //
      // A caller that already HAS the row (the board, search) still opens
      // synchronously below — there is nothing to wait for, so it never sees the
      // spinner.
      if (!providedAlgoliaListing && listingId) {
        setAlgoliaListing(undefined);
        setIsResolving(true);
        setOpen(true);

        const fetched = await fetchListingById(listingId);
        if (requestRef.current !== request) return;

        if (!fetched) {
          // ⚠️ NO MIRROR ROW IS NOT "NO LISTING". The mirror holds a realtor's listing
          // only while it is `onSale`: the moment it is rented out, sold, put in
          // negotiation or frozen, its row is deleted. The realtor's own links to it
          // keep arriving all the same — the lease-end and exclusivity WhatsApp
          // reminders both open `/dashboard/listings/<id>` — and this branch used to
          // answer every one of them by closing the dialog, i.e. a button that did
          // nothing. So before giving up, read the real document.
          //
          // Only for someone who may edit it (an owner, or their office's admin): that
          // is who such a link is sent to, and the editable dialog is the one view of
          // an off-market listing this provider has. Anyone else ends where they
          // always did.
          const ownListing = await resolveWebotListing(listingId);
          if (requestRef.current !== request) return;

          setIsResolving(false);

          if (ownListing && mayEditListing(ownListing, viewerRef.current)) {
            setWebotListing(ownListing);
            setCanEditWebotListing(true);
            return;
          }

          // Nothing to show. Closing restores the pre-2026-09-30 end state
          // ("the click did nothing") rather than parking an empty dialog.
          console.warn("openListingDialog: listing not found", { listingId, source });
          setOpen(false);
          return;
        }

        setIsResolving(false);
        setAlgoliaListing(fetched);
        return;
      }

      setIsResolving(false);
      setAlgoliaListing(providedAlgoliaListing);
      setOpen(true);
    },
    [resolveWebotListing]
  );

  const handleClose = useCallback((): void => {
    // Abandons any in-flight document read so it cannot re-open what the user
    // just dismissed.
    requestRef.current++;
    setIsResolving(false);
    setEditOpen(false);
    setEditDuplicating(false);
    setOpen(false);
  }, []);

  const handleEditListingDialogOpen = useCallback(
    (type?: "edit" | "duplicate") => (): void => {
      setEditDuplicating(type === "duplicate");
      setEditOpen(true);
    },
    []
  );

  const handleEditCancel = useCallback((): void => {
    setEditOpen(false);
    setEditDuplicating(false);
  }, []);

  const editedListingId = webotListing?.id;

  const handleEditConfirm = useCallback((): void => {
    setEditOpen(false);
    setEditDuplicating(false);

    if (!editedListingId) return;

    // The details dialog behind the form is still rendering the pre-edit
    // document, so re-read it. One point read, and it is what makes "I edited it
    // and the modal still shows the old value" impossible.
    void resolveWebotListing(editedListingId).then((fresh) => {
      if (!fresh) return;
      setWebotListing((current) => (current?.id === editedListingId ? fresh : current));
      setCanEditWebotListing((current) =>
        editedListingId === fresh.id ? mayEditListing(fresh, viewerRef.current) : current
      );
    });
  }, [editedListingId, resolveWebotListing]);

  // Memoized: the provider re-renders whenever the dialog's own state moves (and
  // now also when auth/firestore context moves), and an inline object literal
  // would hand every consumer a new value each time — re-rendering every board
  // row through their `openListingDialog` dependency.
  const value = useMemo(() => ({ openListingDialog }), [openListingDialog]);

  return (
    <ListingDialogContext.Provider value={value}>
      {children}
      <ListingDetailsDialog
        open={open}
        listing={webotListing}
        // Belt and braces: `isViewMode` is `!listing && !!algoliaListing`, so the
        // two must never be set together.
        algoliaListing={webotListing ? undefined : algoliaListing}
        isResolvingListing={isResolving}
        isAuthorized={canEditWebotListing}
        initialTab={initialTab}
        handleClose={handleClose}
        // The pencil exists only when there is a real document behind it. A
        // market-ad row gets no edit affordance at all, which is the truth.
        handleEditListingDialogOpen={webotListing ? handleEditListingDialogOpen : undefined}
      />
      {/* Mounted only while open so the form resets its internal state between
          listings — the same shape the board rows use for the duplicate flow,
          and the reason the listings PAGE's always-mounted copy needs an
          explicit reset. */}
      {editOpen && webotListing && (
        <NewListingDialog
          open
          listing={webotListing}
          duplicating={editDuplicating}
          isAuthorized={canEditWebotListing}
          onClose={handleEditCancel}
          onConfirm={handleEditConfirm}
        />
      )}
    </ListingDialogContext.Provider>
  );
}

export function useOpenListingDialog(): (params: OpenListingDialogParams) => Promise<void> {
  return useContext(ListingDialogContext).openListingDialog;
}
